import { IdParams } from '../dto/common.js';
import { DocIdParams } from '../dto/documents.js';
import { listOf } from '../dto/list.js';
import { ConversionReportV1, ConvertFieldRequestV1, DocTypeV1, FieldCreateRequestV1, FieldDefV1, FieldUpdateRequestV1, QuarantinedValueV1, RetypeReportV1, RetypeRequestV1, TypeCreateRequestV1, TypeUpdateRequestV1, } from '../dto/types.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_CONVERSION_REPORT, EXAMPLE_DOC_TYPE, EXAMPLE_DOC_TYPE_CREATED, EXAMPLE_FIELD_CREATED, EXAMPLE_FIELD_DOMAIN, EXAMPLE_FIELD_EPITHET, EXAMPLE_FIELD_HOLY_SYMBOL, EXAMPLE_QUARANTINED_VALUE, EXAMPLE_RETYPE_REPORT, } from './examples-types.js';
/**
 * Types as data (1.0b deliverable 17), built by 1.B-1: `types.list` in its stage 1, the writes,
 * conversion, retype and quarantine read in its stage 1b. The type and field routes take no
 * document id (`none`); a reference type or a reference type's field is refused by the handler,
 * 403 `reference_read_only`, declared where it can happen.
 */
const deity = EXAMPLE_IDS.typeDeity.id;
const owned = { owner: '1.B-1', since: 'exit-1a', status: 'frozen', auth: 'session' };
/** Every type, canon and reference, with its fields and `documentCount` (a bounded list). */
export const typesList = defineRoute({
    id: 'types.list',
    method: 'GET',
    path: '/api/types',
    response: { kind: 'json', schema: listOf(DocTypeV1) },
    reference: 'none',
    ...owned,
    stage: '1',
    since: 'editor-alpha',
    examples: { response: { items: [EXAMPLE_DOC_TYPE], next: null } },
});
export const typesCreate = defineRoute({
    id: 'types.create',
    method: 'POST',
    path: '/api/types',
    body: TypeCreateRequestV1,
    response: { kind: 'json', schema: DocTypeV1, status: 201 },
    errors: { 422: ['capability_locked', 'invalid_body'] },
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: {
        request: {
            body: {
                name: 'Sea Shrine',
                namePlural: 'Sea Shrines',
                icon: 'shrine',
                color: 'shrine',
                capabilities: ['place', 'discoverable'],
            },
        },
        response: EXAMPLE_DOC_TYPE_CREATED,
    },
});
export const typesUpdate = defineRoute({
    id: 'types.update',
    method: 'PATCH',
    path: '/api/types/:id',
    params: IdParams,
    body: TypeUpdateRequestV1,
    response: { kind: 'json', schema: DocTypeV1 },
    errors: { 403: ['reference_read_only'], 422: ['capability_locked', 'invalid_body'] },
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: {
        request: { params: { id: deity }, body: { namePlural: 'Deities' } },
        response: EXAMPLE_DOC_TYPE,
    },
});
/** Archive a type (idempotent). Its success archives the Deity type others' examples name. */
export const typesArchive = defineRoute({
    id: 'types.archive',
    method: 'POST',
    path: '/api/types/:id/archive',
    params: IdParams,
    response: { kind: 'json', schema: DocTypeV1 },
    errors: { 403: ['reference_read_only'], 422: ['capability_locked', 'invalid_body'] },
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: {
        request: { params: { id: deity } },
        response: { ...EXAMPLE_DOC_TYPE, archived: true },
        consumes: true,
    },
});
/** Restore an archived type; one that is not archived is answered unchanged. */
export const typesRestore = defineRoute({
    id: 'types.restore',
    method: 'POST',
    path: '/api/types/:id/restore',
    params: IdParams,
    response: { kind: 'json', schema: DocTypeV1 },
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: { request: { params: { id: deity } }, response: EXAMPLE_DOC_TYPE },
});
const fieldErrors = {
    403: ['reference_read_only'],
    409: ['field_key_conflict'],
    422: ['invalid_body'],
};
export const fieldsCreate = defineRoute({
    id: 'fields.create',
    method: 'POST',
    path: '/api/fields',
    body: FieldCreateRequestV1,
    response: { kind: 'json', schema: FieldDefV1, status: 201 },
    errors: fieldErrors,
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: {
        request: { body: { docTypeId: deity, label: 'Sacred Animal', kind: 'text', config: {} } },
        response: EXAMPLE_FIELD_CREATED,
    },
});
export const fieldsUpdate = defineRoute({
    id: 'fields.update',
    method: 'PATCH',
    path: '/api/fields/:id',
    params: IdParams,
    body: FieldUpdateRequestV1,
    response: { kind: 'json', schema: FieldDefV1 },
    errors: fieldErrors,
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: {
        request: { params: { id: EXAMPLE_IDS.fieldDeityDomain.id }, body: { label: 'Domain' } },
        response: EXAMPLE_FIELD_DOMAIN,
    },
});
/**
 * Archiving never clashes on a key, so `fields.archive` declares no 409 (CS r2c; a ruled break,
 * DECISIONS 138 item 2); restoring a field whose key a live one now holds does.
 */
const archiveErrors = { 403: ['reference_read_only'] };
const restoreErrors = { 403: ['reference_read_only'], 409: ['field_key_conflict'] };
/** Archive a field (idempotent). */
export const fieldsArchive = defineRoute({
    id: 'fields.archive',
    method: 'POST',
    path: '/api/fields/:id/archive',
    params: IdParams,
    response: { kind: 'json', schema: FieldDefV1 },
    errors: archiveErrors,
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: {
        request: { params: { id: EXAMPLE_IDS.fieldDeityHolySymbol.id } },
        response: { ...EXAMPLE_FIELD_HOLY_SYMBOL, archived: true },
    },
});
/** Restore an archived field (idempotent); its key clashing with a live one answers 409. */
export const fieldsRestore = defineRoute({
    id: 'fields.restore',
    method: 'POST',
    path: '/api/fields/:id/restore',
    params: IdParams,
    response: { kind: 'json', schema: FieldDefV1 },
    errors: restoreErrors,
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: {
        request: { params: { id: EXAMPLE_IDS.fieldDeityEpithet.id } },
        response: { ...EXAMPLE_FIELD_EPITHET, archived: false },
    },
});
const convertErrors = { 403: ['reference_read_only'], 422: ['invalid_body'] };
const convertRequest = {
    params: { id: EXAMPLE_IDS.fieldNpcLoyalty.id },
    body: { kind: 'text', config: {} },
};
/** What a kind change would do to every value, writing nothing. */
export const fieldsConvertPreview = defineRoute({
    id: 'fields.convertPreview',
    method: 'POST',
    path: '/api/fields/:id/convert/preview',
    params: IdParams,
    body: ConvertFieldRequestV1,
    response: { kind: 'json', schema: ConversionReportV1 },
    errors: convertErrors,
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: { request: convertRequest, response: EXAMPLE_CONVERSION_REPORT },
});
/** Change a field's kind; a value that does not convert exactly is set aside, never lost. */
export const fieldsConvert = defineRoute({
    id: 'fields.convert',
    method: 'POST',
    path: '/api/fields/:id/convert',
    params: IdParams,
    body: ConvertFieldRequestV1,
    response: { kind: 'json', schema: ConversionReportV1 },
    errors: convertErrors,
    reference: 'none',
    ...owned,
    stage: '1b',
    examples: { request: convertRequest, response: EXAMPLE_CONVERSION_REPORT, consumes: true },
});
const retypeErrors = {
    409: ['retype_blocked', 'version_conflict', 'document_trashed'],
    422: ['invalid_body'],
};
const retypeRequest = {
    params: { id: EXAMPLE_IDS.noteTideMother.id },
    body: { typeId: deity, baseMetaVersion: 1 },
};
/**
 * What a retype would do, writing nothing. A stale `baseMetaVersion` answers 409
 * `version_conflict` (`conflictRevNo: null`); a reference, archived or `session` target type 422.
 */
export const docsRetypePreview = defineRoute({
    id: 'docs.retypePreview',
    method: 'POST',
    path: '/api/docs/:id/retype/preview',
    params: DocIdParams,
    body: RetypeRequestV1,
    response: { kind: 'json', schema: RetypeReportV1 },
    errors: retypeErrors,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...owned,
    stage: '1b',
    examples: { request: retypeRequest, response: EXAMPLE_RETYPE_REPORT },
});
/** Retype a document: fields map by key and kind; the rest is set aside. */
export const docsRetype = defineRoute({
    id: 'docs.retype',
    method: 'POST',
    path: '/api/docs/:id/retype',
    params: DocIdParams,
    body: RetypeRequestV1,
    response: { kind: 'json', schema: RetypeReportV1 },
    errors: retypeErrors,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...owned,
    stage: '1b',
    examples: { request: retypeRequest, response: EXAMPLE_RETYPE_REPORT, consumes: true },
});
/**
 * A document's set-aside values, newest first (a bounded list). A reference document answers an
 * empty list; an unknown id 404.
 */
export const docsQuarantine = defineRoute({
    id: 'docs.quarantine',
    method: 'GET',
    path: '/api/docs/:id/quarantine',
    params: DocIdParams,
    response: { kind: 'json', schema: listOf(QuarantinedValueV1) },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...owned,
    stage: '1b',
    examples: {
        request: { params: { id: EXAMPLE_IDS.npcOldSef.id } },
        response: { items: [EXAMPLE_QUARANTINED_VALUE], next: null },
    },
});
//# sourceMappingURL=types.js.map